import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Typography,
  Card,
  CardContent,
  Grid,
  TablePagination,
  Button,
} from '@mui/material';
import {
  WaterDrop as WaterIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { PageHeader, Loading, EmptyState } from '../../components/Layout';
import waterReadingService from '../../services/waterReadingService';

const TenantWaterReadings = () => {
  const [readings, setReadings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalReadings, setTotalReadings] = useState(0);

  useEffect(() => {
    fetchReadings();
  }, [page, rowsPerPage]);

  const fetchReadings = async () => {
    try {
      setLoading(true);
      const params = {
        skip: page * rowsPerPage,
        limit: rowsPerPage,
      };

      const response = await waterReadingService.getMyReadings(params);
      setReadings(response.data.items || []);
      setTotalReadings(response.data.total || 0);
    } catch (error) {
      console.error('Failed to load readings:', error);
      if (error.response?.status !== 404) {
        toast.error('Failed to load water readings');
      }
      setReadings([]);
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return 'warning';
      case 'billed':
        return 'info';
      case 'paid':
        return 'success';
      default:
        return 'default';
    }
  };

  // Calculate summary statistics
  const totalConsumption = readings.reduce((sum, r) => sum + parseFloat(r.consumption || 0), 0);
  const totalAmount = readings.reduce((sum, r) => sum + parseFloat(r.amount || 0), 0);
  const pendingCount = readings.filter(r => r.status === 'pending').length;

  if (loading && readings.length === 0) {
    return <Loading message="Loading water usage..." />;
  }

  return (
    <Box>
      <PageHeader
        title="My Water Usage"
        subtitle="View your water consumption history"
        breadcrumbs={[{ label: 'Water Usage' }]}
        action={
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchReadings}
          >
            Refresh
          </Button>
        }
      />

      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" variant="body2" gutterBottom>
                Total Consumption
              </Typography>
              <Typography variant="h4">{totalConsumption.toFixed(2)} m³</Typography>
              <Typography variant="caption" color="textSecondary">
                All Time
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" variant="body2" gutterBottom>
                Total Amount
              </Typography>
              <Typography variant="h4">KES {totalAmount.toLocaleString()}</Typography>
              <Typography variant="caption" color="textSecondary">
                All Time
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" variant="body2" gutterBottom>
                Pending Billing
              </Typography>
              <Typography variant="h4">{pendingCount}</Typography>
              <Typography variant="caption" color="textSecondary">
                Awaiting Bills
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Readings Table */}
      {totalReadings === 0 ? (
        <EmptyState
          icon={WaterIcon}
          title="No Water Readings Yet"
          description="Your water usage history will appear here once readings are recorded."
        />
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell><strong>Room</strong></TableCell>
                <TableCell><strong>Previous (m³)</strong></TableCell>
                <TableCell><strong>Current (m³)</strong></TableCell>
                <TableCell><strong>Consumption (m³)</strong></TableCell>
                <TableCell><strong>Amount (KES)</strong></TableCell>
                <TableCell><strong>Date</strong></TableCell>
                <TableCell><strong>Status</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {readings.map((reading) => (
                <TableRow key={reading.id}>
                  <TableCell>{reading.room_number}</TableCell>
                  <TableCell>{parseFloat(reading.previous_reading).toFixed(2)}</TableCell>
                  <TableCell>{parseFloat(reading.current_reading).toFixed(2)}</TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight="bold" color="primary">
                      {parseFloat(reading.consumption).toFixed(2)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight="bold">
                      {parseFloat(reading.amount).toLocaleString()}
                    </Typography>
                  </TableCell>
                  <TableCell>{new Date(reading.reading_date).toLocaleDateString()}</TableCell>
                  <TableCell>
                    <Chip
                      label={reading.status.toUpperCase()}
                      color={getStatusColor(reading.status)}
                      size="small"
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <TablePagination
            rowsPerPageOptions={[10, 25, 50]}
            component="div"
            count={totalReadings}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </TableContainer>
      )}
    </Box>
  );
};

export default TenantWaterReadings;
